import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import axiosInstance from "../../../Config/axiosInstance";
import "./HistoryClientAddendum.css";
import EditIcon from "@mui/icons-material/Edit";

const formatDate = (dateString) => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

const formatAmountRO = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const raw = String(value).trim();
  const match = raw.match(/-?[\d.,\s]+/);
  if (!match) return raw;

  let normalized = match[0].trim().replace(/\s/g, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const n = Number(normalized);
  if (Number.isNaN(n)) return raw;

  const formatted = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

  const suffix = raw.replace(match[0], "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const formatExchangeRateRO = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  const raw = String(value).trim();
  const match = raw.match(/-?[\d.,\s]+/);
  if (!match) return raw;

  let normalized = match[0].trim().replace(/\s/g, "");

  if (normalized.includes(",") && normalized.includes(".")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (normalized.includes(",")) {
    normalized = normalized.replace(",", ".");
  }

  const n = Number(normalized);
  if (Number.isNaN(n)) return raw;

  const formatted = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(n);

  const suffix = raw.replace(match[0], "").trim();
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const getProjectCodesFromLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return "-";

  const codes = [
    ...new Set(lines.map((line) => line?.project_code_label).filter(Boolean)),
  ];

  return codes.length ? codes.join(", ") : "-";
};

const HistoryClientAddendum = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [contract, setContract] = useState(null);
  const [addendums, setAddendums] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const RAW_API_BASE =
    process.env.REACT_APP_API_URL ||
    axiosInstance.defaults.baseURL ||
    window.location.origin;

  const API_BASE_URL = String(RAW_API_BASE)
    .replace(/\/api\/?$/, "")
    .replace(/\/+$/, "");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [contractRes, addendumsRes] = await Promise.all([
        axiosInstance.get(`/client-contracts/${contractId}/`),
        axiosInstance.get(`/client-addendums/?contract=${contractId}`),
      ]);

      setContract(contractRes.data);
      const loadedAddendums = addendumsRes.data || [];
      setAddendums(loadedAddendums);

      const selectedAddendumId = location.state?.selectedAddendumId;

      if (selectedAddendumId) {
        const sortedAddendums = [...loadedAddendums].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;

          if (dateA !== dateB) {
            return dateA - dateB;
          }

          return Number(a.id) - Number(b.id);
        });

        const addendumIndex = sortedAddendums.findIndex(
          (addendum) => Number(addendum.id) === Number(selectedAddendumId)
        );

        setTabIndex(addendumIndex >= 0 ? addendumIndex + 1 : 0);
      } else {
        setTabIndex(0);
      }
    } catch (error) {
      console.error("Error fetching client addendum history:", error);
      setErrorMessage("Failed to fetch addendum history.");
    } finally {
      setLoading(false);
    }
  }, [contractId, location.state]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const historyItems = useMemo(() => {
    const items = [];

    if (contract) {
      items.push({
        ...contract,
        history_type: "original",
        history_label: contract.nr || "Original Contract",
      });
    }

    const sortedAddendums = [...addendums].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;

      if (dateA !== dateB) {
        return dateA - dateB;
      }

      return Number(a.id) - Number(b.id);
    });

    const latestAddendumId = sortedAddendums.length
      ? sortedAddendums[sortedAddendums.length - 1].id
      : null;

    sortedAddendums.forEach((addendum, index) => {
      const addendumNumber = index + 1;
      const isLatestAddendum = addendum.id === latestAddendumId;

      items.push({
        ...addendum,
        history_type: "addendum",
        history_label:
          addendum.nr ||
          `Addendum ${addendumNumber}`,
        addendum_display_number: addendumNumber,
        is_latest_addendum: isLatestAddendum,
      });
    });

    return items;
  }, [contract, addendums]);

  const activeItem = useMemo(() => {
    if (!historyItems.length) return null;
    return historyItems[tabIndex] || null;
  }, [historyItems, tabIndex]);

  const getAttachmentList = (item) => {
    if (!item) return [];
    if (Array.isArray(item.attach_files)) return item.attach_files;
    if (Array.isArray(item.attachments)) return item.attachments;
    if (Array.isArray(item.files)) return item.files;
    return [];
  };

  const matchPageProtocol = (url) => {
    if (!url) return url;

    if (typeof window !== "undefined" && window.location.protocol === "https:") {
      return url.replace(/^http:\/\//i, "https://");
    }

    return url;
  };

  const getFileUrl = (file) => {
    if (!file) return null;

    if (file.file_url) return matchPageProtocol(file.file_url);
    if (file.url) return matchPageProtocol(file.url);

    if (file.file) {
      const rawPath = String(file.file);

      if (/^https?:\/\//i.test(rawPath)) {
        return matchPageProtocol(rawPath);
      }

      const normalizedPath = rawPath.startsWith("/")
        ? rawPath
        : `/media/${rawPath.replace(/^media\//, "")}`;

      return matchPageProtocol(`${API_BASE_URL}${normalizedPath}`);
    }

    return null;
  };

  const getExistingFileName = (file) => {
    if (file?.filename) return file.filename;

    if (typeof file?.file === "string") {
      const parts = file.file.split("/");
      return parts[parts.length - 1];
    }

    return "download";
  };

  const isPdfExistingFile = (file) => {
    const filename = getExistingFileName(file).toLowerCase();
    return filename.endsWith(".pdf");
  };

  const getFileIcon = (file) => {
    const filename = file?.filename || file?.file?.name || file?.file || "";
    const extension = filename.split(".").pop()?.toLowerCase() || "";

    if (extension === "pdf") {
      return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
    }

    if (["doc", "docx"].includes(extension)) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
    }

    if (["xls", "xlsx"].includes(extension)) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
    }

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
    }

    return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
  };

  const handleFileClick = async (file) => {
    const fileUrl = getFileUrl(file);

    if (!fileUrl) {
      setErrorMessage("File URL not available.");
      return;
    }

    try {
      const response = await fetch(fileUrl);

      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const filename = getExistingFileName(file);

      if (isPdfExistingFile(file)) {
        const newWindow = window.open();

        if (!newWindow) {
          URL.revokeObjectURL(objectUrl);
          setErrorMessage("Popup blocked. Please allow popups for PDF preview.");
          return;
        }

        newWindow.document.write(`
          <html>
            <head>
              <title>${filename}</title>
              <style>
                body { margin: 0; height: 100vh; }
                embed { width: 100%; height: 100%; }
              </style>
            </head>
            <body>
              <embed src="${objectUrl}" type="application/pdf" />
            </body>
          </html>
        `);

        newWindow.document.close();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } else {
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
    } catch (error) {
      console.error("Error opening attachment:", error);
      setErrorMessage("Could not open/download the selected file.");
    }
  };

  if (loading) {
    return (
      <div className="history-offer-page">
        <div className="history-offer-loading">
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (errorMessage && !historyItems.length) {
    return (
      <div className="history-offer-page">
        <Alert severity="error">{errorMessage}</Alert>
      </div>
    );
  }

  if (!activeItem) {
    return (
      <div className="history-offer-page">
        <Alert severity="info">No history available for this contract.</Alert>
      </div>
    );
  }

  const attachmentList = getAttachmentList(activeItem);
  const isOriginal = activeItem.history_type === "original";

  return (
    <div className="history-offer-page">
      <div className="history-offer-header">
        <div className="history-offer-header-left">
          <ArrowBackIcon
            className="back-arrow"
            onClick={() => navigate("/client_contracts")}
          />
          <div className="history-offer-title-wrap">
            <h2 className="history-offer-title">CLIENT CONTRACT HISTORY</h2>

            <p className="history-offer-subtitle">
              <span className="history-offer-subtitle-icon">✨</span>
              <span className="history-offer-subtitle-text">
                Original contract and all addendums for the selected client contract.
              </span>
            </p>
          </div>
        </div>
      </div>

      <Divider className="history-offer-header-divider" />

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Paper className="history-offer-tabs-card" elevation={0}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          className="history-offer-tabs"
        >
          {historyItems.map((item) => (
            <Tab
              key={`${item.history_type}-${item.id}`}
              label={item.history_label}
              className="history-offer-tab"
            />
          ))}
        </Tabs>
      </Paper>

      <Paper className="history-offer-content-card" elevation={0}>
        <div className="history-offer-top">
          <div className="history-offer-top-left">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h6" className="history-offer-code">
                {activeItem.nr || "-"}
              </Typography>

              {/* Afișează statusul atât pentru contract cât și pentru addendum */}
              <span
                className={`status-pill status-${(activeItem.status || "active").toLowerCase()}`}
              >
                {activeItem.status || "Active"}
              </span>
            </div>
          </div>

          <div className="history-offer-top-right">
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              className="edit-addendum-btn"
              onClick={() => {
                if (isOriginal) {
                  navigate(`/client_contracts/edit-client-contracts/${activeItem.id}`);
                } else {
                  navigate(`/client_contracts/edit-addendum/${activeItem.id}`);
                }
              }}
            >
              Edit
            </Button>

            <Typography
              variant="body2"
              className={`history-offer-current-tag ${isOriginal
                ? "history-offer-current-tag--current"
                : "history-offer-current-tag--historical"
                }`}
            >
              {isOriginal ? "Original contract" : "Addendum"}
            </Typography>
          </div>
        </div>

        <Divider className="history-offer-divider" />

        <Box className="history-offer-grid">
          <div className="history-offer-card">
            <span className="history-offer-label">Client</span>
            <span className="history-offer-value">
              {activeItem.client_name || contract?.client_name || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Client Document</span>
            <span className="history-offer-value">
              {activeItem.client_document || "-"}
            </span>
          </div>

          {isOriginal && (
            <div className="history-offer-card">
              <span className="history-offer-label">Contract Type</span>
              <span className="history-offer-value">
                {activeItem.contract_type || "-"}
              </span>
            </div>
          )}

          <div className="history-offer-card">
            <span className="history-offer-label">Sign Date</span>
            <span className="history-offer-value">
              {formatDate(activeItem.sign_date)}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">End Date</span>
            <span className="history-offer-value">
              {formatDate(activeItem.end_date)}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Responsible Person</span>
            <span className="history-offer-value">
              {activeItem.responsible_person_label || "-"}
            </span>
          </div>

          <div className="history-offer-card">
            <span className="history-offer-label">Project Code</span>
            <span className="history-offer-value">
              {activeItem.project_code_label ||
                getProjectCodesFromLines(activeItem.lines)}
            </span>
          </div>

          <div
            className={`history-offer-card ${!isOriginal ? "history-offer-card-half" : ""
              }`}
          >
            <span className="history-offer-label">Total</span>
            <span className="history-offer-value">
              {formatAmountRO(activeItem.total_contract_display || "-")}
            </span>
          </div>

          <div
            className={`history-offer-card ${!isOriginal ? "history-offer-card-half" : ""
              }`}
          >
            <span className="history-offer-label">Total System</span>
            <span className="history-offer-value">
              {formatAmountRO(activeItem.total_system_contract || "-")}
            </span>
          </div>

          <div className="history-offer-card history-offer-card-full">
            <span className="history-offer-label">Object</span>
            <span className="history-offer-value">
              {activeItem.object || "-"}
            </span>
          </div>
        </Box>

        <div className="history-offer-section">
          <h3>Opportunity Lines</h3>

          <div className="history-offer-table-wrapper">
            <table className="history-offer-table">
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Project Code</th>
                  <th>Subproject Code</th>
                  <th>Currency</th>
                  <th>Exchange Rate</th>
                  <th className="col-sum">Total</th>
                  <th className="col-system-sum">Total System</th>
                </tr>
              </thead>

              <tbody>
                {(activeItem.lines || []).length > 0 ? (
                  activeItem.lines.map((line) => (
                    <tr key={line.id || line.opportunity}>
                      <td>{line.opportunity_label || "-"}</td>
                      <td>{line.project_code_label || "-"}</td>
                      <td>{line.subproject_code_label || "-"}</td>
                      <td>{line.currency_label || "-"}</td>
                      <td>
                        {formatExchangeRateRO(line.exchange_rate_value || "-")}
                      </td>
                      <td>
                        {formatAmountRO(line.total_opportunities_contract || "-")}
                      </td>
                      <td>
                        {formatAmountRO(line.total_opp_contract_system || "-")}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="history-offer-empty-row">
                      No opportunity lines available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="history-offer-section">
          <h3>Attachments</h3>

          {attachmentList.length > 0 ? (
            <div className="history-offer-files-list">
              {attachmentList.map((file, index) => (
                <div
                  key={file.id || index}
                  className="history-offer-file-item"
                >
                  <div className="history-offer-file-info">
                    {getFileIcon(file)}

                    <span
                      className="history-offer-file-name clickable"
                      title={
                        isPdfExistingFile(file)
                          ? "Click to preview PDF"
                          : "Click to download file"
                      }
                      onClick={() => handleFileClick(file)}
                    >
                      {getExistingFileName(file)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="history-offer-empty-files">
              No attachments available.
            </div>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default HistoryClientAddendum;